import { z } from 'zod';
import type { Mode, TenantId } from './types';

/**
 * Runtime configuration injection.
 *
 * Vite's `import.meta.env` is **build-time only** - values are baked into
 * the bundle when `vite build` runs. The K8s deployment manifest at
 * `kubernetes-resources/dev/care-pathway-studio.yaml` sets
 * `PROTOTYPE_TENANT_DEFAULT`, `PROTOTYPE_MODE`, and `BUILD_SHA` as
 * **runtime** container env vars, so those cannot reach the browser via
 * `import.meta.env`.
 *
 * Runtime path (production): the container entrypoint
 * (`docker-entrypoint.sh`) templates the env vars into
 * `/usr/share/nginx/html/config.json` before exec'ing nginx. The SPA
 * fetches `/config.json` at boot in `main.tsx` and hydrates the stores
 * before the first `createRoot(...).render(<App />)` call.
 *
 * Local dev: `public/config.json` ships a default copy so `npm run dev`
 * works without the entrypoint script.
 */

const TENANT_IDS = ['apex-cardiometabolic', 'longevity-health-institute'] as const;
const MODES = ['demo', 'live'] as const;

export const runtimeConfigSchema = z.object({
  tenantDefault: z.enum(TENANT_IDS),
  mode: z.enum(MODES),
  buildSha: z.string(),
  /**
   * Application Insights connection string injected at runtime by the K8s
   * deployment manifest (PROD-573 INFRA GATE follow-up). The Helm chart /
   * docker-entrypoint templates `APPLICATIONINSIGHTS_CONNECTION_STRING`
   * into `/config.json`. The field is nullable so dev / preview / Demo
   * Mode boots cleanly without the secret wired.
   */
  appInsightsConnectionString: z.string().nullable().default(null),
  /**
   * Application Insights / Log Analytics workspace id. Required by the
   * Live `AppInsightsMetricsClient` (PROD-592) to query the customEvents
   * table via KQL. Nullable so Demo Mode and dev boot cleanly without
   * the secret wired; when null the metrics client falls back to the
   * `DemoMetricsClient`. Provisioned via the PROD-593 INFRA GATE step.
   */
  appInsightsWorkspaceId: z.string().nullable().default(null),
  /**
   * Postgres connection string for the curated corpus database
   * (PROD-574 + PROD-590). Server-side scripts (`scripts/ingest-corpus.ts`)
   * read this from process.env, NOT from this runtime config; the value
   * is surfaced here only so the boot path can record whether the corpus
   * URL is configured for diagnostic purposes. Browser code MUST NOT
   * dial the database directly. Nullable so non-Live boots are clean.
   */
  databaseUrl: z.string().nullable().default(null),
});

export type RuntimeConfig = z.infer<typeof runtimeConfigSchema>;

export const tenantIdSchema = z.enum(TENANT_IDS);
export const modeSchema = z.enum(MODES);

export const FALLBACK_RUNTIME_CONFIG: RuntimeConfig = {
  tenantDefault: 'apex-cardiometabolic',
  mode: 'demo',
  buildSha: 'unknown',
  appInsightsConnectionString: null,
  appInsightsWorkspaceId: null,
  databaseUrl: null,
};

/**
 * Fetch and validate `/config.json`. On any failure (network, 404, parse,
 * schema mismatch) returns `FALLBACK_RUNTIME_CONFIG` and logs a warning.
 * Boot must not block - degraded boot with sync defaults is preferable to
 * a blank screen.
 */
export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  try {
    const res = await fetch('/config.json', { cache: 'no-store' });
    if (!res.ok) {
      console.warn(
        `[runtime-config] /config.json returned ${res.status}; using fallback defaults`,
      );
      return FALLBACK_RUNTIME_CONFIG;
    }
    const raw = (await res.json()) as unknown;
    const parsed = runtimeConfigSchema.safeParse(raw);
    if (!parsed.success) {
      console.warn(
        '[runtime-config] /config.json failed schema validation; using fallback defaults',
        parsed.error.flatten(),
      );
      return FALLBACK_RUNTIME_CONFIG;
    }
    return parsed.data;
  } catch (err) {
    console.warn(
      '[runtime-config] failed to fetch/parse /config.json; using fallback defaults',
      err,
    );
    return FALLBACK_RUNTIME_CONFIG;
  }
}

/** Validate a tenant id at runtime. Returns null if invalid. */
export function parseTenantId(id: unknown): TenantId | null {
  const parsed = tenantIdSchema.safeParse(id);
  return parsed.success ? parsed.data : null;
}

/** Validate a mode at runtime. Returns null if invalid. */
export function parseMode(value: unknown): Mode | null {
  const parsed = modeSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}
